import { useCallback, useEffect, useRef, useState } from 'react';
import { API } from '../../api';
import { LessonVideoStatus, isTerminalJob } from './types';

/**
 * How long this hook keeps asking about a render that never reaches a terminal
 * status. The backend fails an abandoned job itself, after 10 minutes queued
 * or 35 without a progress write, and that answer arrives here like any other
 * status; this is the backstop for when it never does. Past it the card says
 * so and offers a manual check, rather than animating forever.
 */
const POLL_DEADLINE_MS = 40 * 60 * 1000;

/**
 * Lesson video status (attachment, resumable project, active render job,
 * allowance, brand kit) with polling while a render is in flight. Server
 * state stays server-backed (§59): every mutation calls `refetch`.
 */
export function useLessonVideo(programId: string, moduleId: string, lessonId: string, enabled = true) {
  const [status, setStatus] = useState<LessonVideoStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  // Bumped by `recheck` to restart the poll after it gave up.
  const [pollEpoch, setPollEpoch] = useState(0);
  const alive = useRef(true);

  const refetch = useCallback(async () => {
    if (!enabled || !programId || !moduleId || !lessonId) return null;
    try {
      setLoading(true);
      const res: any = await API.getLessonVideoBuilderStatus({ programId, moduleId, lessonId });
      if (!alive.current) return null;
      setStatus(res.data as LessonVideoStatus);
      setError(null);
      return res.data as LessonVideoStatus;
    } catch (err: any) {
      if (!alive.current) return null;
      setError(err?.response?.data?.message || 'Could not load the lesson video');
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [enabled, programId, moduleId, lessonId]);

  useEffect(() => {
    alive.current = true;
    setStatus(null);
    refetch();
    return () => {
      alive.current = false;
    };
  }, [refetch]);

  // Poll while a render job is active.
  const activeJobId =
    status?.activeJob && !isTerminalJob(status.activeJob.status) ? status.activeJob.renderJobId : null;
  useEffect(() => {
    if (!activeJobId) {
      setStalled(false);
      return undefined;
    }
    setStalled(false);
    const startedAt = Date.now();
    const t = setInterval(() => {
      if (Date.now() - startedAt >= POLL_DEADLINE_MS) {
        clearInterval(t);
        if (alive.current) setStalled(true);
        return;
      }
      refetch();
    }, 3000);
    return () => clearInterval(t);
  }, [activeJobId, refetch, pollEpoch]);

  // Asking again after we gave up: one read now, and the poll starts over with
  // a fresh deadline if the render really is still going.
  const recheck = useCallback(() => {
    setPollEpoch((n) => n + 1);
    return refetch();
  }, [refetch]);

  // `stalled`: the render is still reported as running but stopped being worth
  // waiting on. The caller owns what to show; nothing here keeps polling.
  return { status, loading, error, stalled, refetch, recheck, setStatus };
}
